import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslations } from 'next-intl';
import { Point, type FabricObject, type Group, type FabricText } from 'fabric';

// Utilities
import copyItem from '@/utils/editor/copyItem';
import removeActiveItem from '@/utils/editor/removeActiveItem';
import rotateItem from '@/utils/editor/rotateItem';

// Context
import { useToolContext, type Selection } from '@/context/ToolContext';

// Shirt variations whose canvas badge carries a letter instead of a squad number (Trainer, and
// Dugout's Scheidsrechter). Both render the plain `shirt.svg` — the letter is the only thing telling
// them apart on the canvas, so it is stamped from `DRWNG__ABBR_<variation>` on first selection.
// Every entry MUST have that key in each locale, or the badge renders the bare key
// (see `getMessageFallback` in i18n/request.ts).
export const LETTER_BADGE_VARIATIONS = new Set([
  'shirt-trainer',
  'shirt-scheidsrechter',
]);

interface EditorProps {
  // The Fabric selection: an array of selected objects, a line-type variation string, or null —
  // sourced from the ToolContext `selection` value (same `Selection` type).
  selection: Selection;
  iconType: string;
  onEditorInputBlur: () => void;
}

const Editor = (props: EditorProps) => {
  const { selection, iconType, onEditorInputBlur } = props;

  const ToolContextState = useToolContext();

  const t = useTranslations();

  const editorInputRef = useRef<HTMLInputElement>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [variation, setVariation] = useState<string | null>(null);
  const [shirtInputVal, setShirtInputVal] = useState<string | null>(null);
  // eslint-disable-next-line unicorn/no-non-function-verb-prefix -- `removeOnly` is a boolean mode flag (delete-only Editor), not a function; the `remove` prefix is coincidental
  const [removeOnly, setRemoveOnly] = useState(false);
  const [originalViewportHeight, setOriginalViewportHeight] = useState(
    window.innerHeight,
  );
  const [originalViewportWidth, setOriginalViewportWidth] = useState(
    window.innerWidth,
  );

  const [selectedShirt, setSelectedShirt] = useState<
    FabricObject | FabricObject[] | null
  >(null);
  const [arrowIcon, setArrowIcon] = useState<string | null>(null);

  // `selection` is polymorphic (see the `Selection` type). A string selection is ALWAYS a line-type
  // variation (it includes 'line'), so `typeof … === 'string'` is the line-selection test.
  const isLineSelection = typeof selection === 'string';
  const selectedObjects = Array.isArray(selection) ? selection : [];
  const selectionString = typeof selection === 'string' ? selection : '';

  const handleRemove = () => {
    removeActiveItem(ToolContextState.canvasStateRef.current);
  };

  // Keyboard Delete / Backspace removes the active object. Bound ONCE on mount (empty deps): handleRemove
  // reads the canvas via a stable ref, so no selection dependency is needed. The Backspace branch stays
  // suppressed while the shirt-number input is focused, so editing a number never deletes the shirt.
  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Delete') {
        handleRemove();
      } else if (
        event.key === 'Backspace' &&
        !document.activeElement?.classList.contains('Editor-input')
      ) {
        event.preventDefault();
        handleRemove();
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  useEffect(() => {
    if (!selection) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset when the selection clears
      setIsVisible(false);
      setSelectedShirt([]);
      return;
    }
    // A string selection is a line-type — show the Editor and stop (the object branch below is for
    // array selections only). Narrowing `selection` here also types the `.length` / `for…of` below.
    if (typeof selection === 'string') {
      if (selection.includes('line')) setIsVisible(true);
      return;
    }
    setRemoveOnly(selection.length > 1);
    setIsVisible(true);

    for (const item of selection) {
      const itemVariation = item.variation;

      if (itemVariation?.includes('shirt')) {
        setSelectedShirt(item);
        setVariation('shirt');

        // The shirt is a Group; its number lives in the child text object.
        const textObj = (item as Group).getObjects('text')[0] as FabricText;

        setShirtInputVal(textObj.text);

        if (LETTER_BADGE_VARIATIONS.has(itemVariation) && textObj.text === '') {
          setShirtInputVal(t(`DRWNG__ABBR_${itemVariation}`));
          textObj.set('text', t(`DRWNG__ABBR_${itemVariation}`));
          ToolContextState.canvasStateRef.current.requestRenderAll();
        }
      }

      if (itemVariation?.includes('goal')) setVariation('goal');
      if (itemVariation?.includes('line')) setVariation('line');
      if (itemVariation?.includes('ball')) setVariation('ball');
      if (itemVariation?.includes('cone')) setVariation('cone');
      if (itemVariation?.includes('witches-hat')) setVariation('witches-hat');
      if (itemVariation?.includes('tool')) setVariation('tool');
    }
  }, [selection]);

  useEffect(() => {
    if (variation === 'shirt' && window.visualViewport) {
      // eslint-disable-next-line unicorn/prefer-observer-apis -- visualViewport 'resize' fires on keyboard show/hide; ResizeObserver observes element box size, not the visual viewport, so it is not equivalent
      window.visualViewport.addEventListener('resize', keyboardShowHandler);
    }
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          keyboardShowHandler,
        );
      }
    };
  }, [selectedShirt]);

  const keyboardShowHandler = () => {
    const vpw = window.visualViewport!.width;
    const vph = window.visualViewport!.height;
    if (vph < originalViewportHeight && vpw === originalViewportWidth) {
      // A shirt is selected in this handler (it is only bound while variation === 'shirt').
      const shirt = selectedShirt as FabricObject;
      const x = shirt.left - vpw / 2;
      const y = shirt.top - vph / 2;

      let calcY: number | undefined;
      const agent = navigator ? navigator?.userAgent : false;
      if (agent) {
        if (agent.includes('iPhone')) calcY = y - vph;
        else if (agent.includes('iPad')) calcY = y - (vph - 120);
        else calcY = y;
      }
      // Keyboard-open pan (device-only — not reproducible in the headless golden, per the net-blind
      // register). `absolutePan` reads only `.x`/`.y`, so the plain `{x,y}` object works at runtime;
      // the cast preserves that exact object rather than constructing a Point on this unverifiable path.
      ToolContextState.canvasStateRef.current.absolutePan({
        x: x,
        y: calcY,
      } as unknown as Point);
    }
    setOriginalViewportHeight(vph);
    setOriginalViewportWidth(vpw);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const textValue = event.target.value;
    setShirtInputVal(textValue);
    (selectedShirt as Group | null)
      ?.getObjects('text')[0]
      .set('text', textValue);
    ToolContextState.canvasStateRef.current.requestRenderAll();
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      onEditorInputBlur();
    }, 1000);
  };

  useEffect(() => {
    for (const line of ToolContextState.lineTypes) {
      if (
        line.variation === selection ||
        line.variation === selectedObjects[0]?.variation
      ) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- derived from the current selection on selection change
        setArrowIcon(line.defaultIcon.src);
      }
    }
  }, [selection]);

  const handleRotate = () => {
    rotateItem(ToolContextState.canvasStateRef.current);
  };
  const handleCopy = () => {
    copyItem(
      ToolContextState.canvasStateRef.current,
      iconType,
      ToolContextState.isMobileStateRef.current,
      0.95,
      ToolContextState.theme,
    );
  };
  const handleClose = () => {
    if (variation === 'shirt') {
      ToolContextState.canvasStateRef.current.absolutePan(new Point(0, 0));
      ToolContextState.canvasStateRef.current.setZoom(1);
      ToolContextState.setReloadField(true);
    } else if (isLineSelection) {
      ToolContextState.setLineSelected(null);
      ToolContextState.setStoreLineType('');
    }
    ToolContextState.setSelection(null);
  };

  const variationCaption = selectedObjects[0]?.variation || '';

  return (
    <>
      <div className={`Editor${isVisible ? ' Editor--visible' : ''}`}>
        {isLineSelection && (
          <>
            <img
              className="Editor-arrowIcon"
              src={arrowIcon!}
              alt={selectionString}
            />
            <span className="Editor-caption">
              {t(`DRWNG__ITEM_${selectionString}`)}
            </span>
            <span className="Editor-startDrawing">
              {t('DRWNG__EDTR_START')}
            </span>
          </>
        )}
        {!removeOnly && variation !== 'line' && (
          <span className="Editor-caption">
            {variationCaption === ''
              ? ''
              : t(`DRWNG__ITEM_${variationCaption}`)}
          </span>
        )}
        {!removeOnly && variation === 'line' && (
          <>
            {ToolContextState.isMobileStateRef.current && (
              <img
                className="Editor-arrowIcon"
                src={arrowIcon!}
                alt={selectionString}
              />
            )}
            <span className="Editor-caption">
              {variationCaption === ''
                ? ''
                : t(`DRWNG__ITEM_${variationCaption}`)}
            </span>
          </>
        )}

        {variation === 'shirt' && !removeOnly && (
          <div className="Editor-inputWrapper">
            <span className="Editor-inputName">
              {t(
                selectedObjects[0]?.variation === 'shirt-annotation'
                  ? 'DRWNG__EDTR_ORDERNUMBER'
                  : 'DRWNG__EDTR_NUMBER',
              )}
            </span>
            <input
              ref={editorInputRef}
              type="text"
              className="Editor-input"
              aria-label={t(
                selectedObjects[0]?.variation === 'shirt-annotation'
                  ? 'DRWNG__EDTR_ORDERNUMBER'
                  : 'DRWNG__EDTR_NUMBER',
              )}
              maxLength={2}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              value={shirtInputVal ?? ''}
            />
          </div>
        )}
        {variation === 'goal' && !removeOnly && (
          <button onClick={handleRotate} className="Button--editor">
            <p>{t('DRWNG__EDTR_ROTATE')}</p>
            <svg className="Editor-icon" viewBox="0 0 12 14">
              <path
                d="M10 8a4 4 0 0 1-4 4 4 4 0 0 1-4-4 4 4 0 0 1 4-4v2l4-3-4-3v2a6 6 0 0 0-6 6 6 6 0 0 0 6 6 6 6 0 0 0 6-6h-2Z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
        {variation !== 'line' &&
          !removeOnly &&
          !isLineSelection &&
          !ToolContextState.isMobileStateRef.current && (
            // lines do not get a copy button, everything else does
            <button
              onClick={handleCopy}
              className="Button--editor"
              aria-label={t('DRWNG__A11Y_COPY')}
            >
              <svg className="Editor-icon" viewBox="0 0 14 14">
                <g fill="currentColor">
                  <path d="M0 14h10V4H0v10Zm2-2h6V6H2v6Z" />
                  <path d="M4 0v3h2V2h6v6h-1v2h3V0z" />
                </g>
              </svg>
            </button>
          )}
        {!isLineSelection && !ToolContextState.isMobileStateRef.current && (
          <button
            onClick={handleRemove}
            className="Button--editor"
            aria-label={t('DRWNG__A11Y_REMOVE')}
          >
            <svg className="Editor-icon" viewBox="0 0 10 11">
              <path
                d="M7 1V0H3v1H0v2h1v8h8V3h1V1H7ZM4 9H3V3h1v6Zm3 0H6V3h1v6Z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
      </div>
      {ToolContextState.isMobileStateRef.current && (
        <div className={`Editor--mobile${isVisible ? ' Editor--visible' : ''}`}>
          {variation !== 'line' && !removeOnly && !isLineSelection && (
            // lines do not get a copy button, everything else does
            <button
              onClick={handleCopy}
              className="Button--editor Button--mobile"
              aria-label={t('DRWNG__A11Y_COPY')}
            >
              <svg className="Editor-icon" viewBox="0 0 14 14">
                <g fill="currentColor">
                  <path d="M0 14h10V4H0v10Zm2-2h6V6H2v6Z" />
                  <path d="M4 0v3h2V2h6v6h-1v2h3V0z" />
                </g>
              </svg>
            </button>
          )}
          {!isLineSelection && ToolContextState.isMobileStateRef.current && (
            <button
              onClick={handleRemove}
              className="Button--editor Button--mobile"
              aria-label={t('DRWNG__A11Y_REMOVE')}
            >
              <svg className="Editor-icon" viewBox="0 0 10 11">
                <path
                  d="M7 1V0H3v1H0v2h1v8h8V3h1V1H7ZM4 9H3V3h1v6Zm3 0H6V3h1v6Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          )}
          {ToolContextState.isMobileStateRef.current && (
            <button
              onClick={handleClose}
              className="Button--editor Button--mobile Button--editorClose"
              aria-label={t('DRWNG__A11Y_CLOSE')}
            >
              <svg
                width="18"
                height="13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 18 13"
              >
                <path
                  d="m6.7 10 9.8-10L18 1.5 6.7 13 0 6.1l1.5-1.5 5.2 5.3Z"
                  fill="#fff"
                />
              </svg>
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default Editor;
